import { useState } from 'react';
import { X, AlertCircle, Loader } from 'lucide-react';
import { incidentsApiService } from '../services/incidentsApi';
import { IncidentStatus } from '../types/incident';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownEditor from './MarkdownEditor';

interface CreateIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const STATUS_OPTIONS: IncidentStatus[] = ['active', 'critical', 'pending', 'investigating'];
const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const CreateIncidentModal = ({ isOpen, onClose, onSuccess }: CreateIncidentModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<IncidentStatus>('active');
  const [priority, setPriority] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await incidentsApiService.createIncident({
        title,
        description,
        status,
        priority,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setStatus('active');
    setPriority(5);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 className="modal-title">Create New Incident</h2>
            <button onClick={handleClose} className="modal-close" title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="glass-input w-full"
                placeholder="Brief incident title..."
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Description <span className="text-red-400">*</span>
              </label>
              <MarkdownEditor
                value={description}
                onChange={setDescription}
                height={300}
                placeholder="Enter incident description in markdown..."
              />
              <p className="text-xs text-glass-500 mt-1">
                Markdown editor with live preview - use code blocks, tables, lists, etc.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as IncidentStatus)}
                  className="glass-input w-full"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Priority (1 = highest, 10 = lowest)
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="glass-input w-full"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>Priority {p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={handleClose}
                className="glass-button-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || !title || !description}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Incident'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

